/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useRoleProfilePermissions.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing role profile permissions with hierarchical inheritance.
    Uses moduleSlug (string) to match permissions with menu items.
────────────────────────────────────────────────────────────────*/

import { RoleProfileFormData } from '../types';

interface MenuItem {
  id: number;
  slug?: string;
  level: number;
  parentId: number | null;
}

export function useRoleProfilePermissions(
  formData: RoleProfileFormData,
  menuItems: MenuItem[]
) {
  // Get the slug for a menu item by its id
  const getSlug = (menuItemId: number): string | undefined => {
    return menuItems.find(m => m.id === menuItemId)?.slug;
  };

  const getPermissionLevelForMenuItem = (menuItemId: number): 'FULL' | 'READ' | 'NONE' => {
    const slug = getSlug(menuItemId);
    if (!slug) return 'NONE';
    const permission = formData.permissions.find(p => p.moduleSlug === slug);
    return permission ? permission.level : 'NONE';
  };

  // Helper to check if a menu item is a "leaf" (level 3 OR level 2 without level 3 children)
  const isLeafItem = (itemId: number): boolean => {
    const item = menuItems.find(m => m.id === itemId);
    if (!item) return false;
    if (item.level === 3) return true;
    if (item.level === 2) {
      const hasLevel3Children = menuItems.some(
        child => child.parentId === itemId && child.level === 3
      );
      return !hasLevel3Children;
    }
    return false;
  };

  const allChildrenHavePermission = (parentId: number, level: 'FULL' | 'READ' | 'NONE'): boolean => {
    const directChildren = menuItems.filter(item => item.parentId === parentId);
    if (directChildren.length === 0) return false;

    return directChildren.every(child => {
      // If child is a leaf item (level 3 or level 2 without children), check its direct permission
      if (isLeafItem(child.id)) {
        return getPermissionLevelForMenuItem(child.id) === level;
      }
      // Otherwise, recurse into child's children
      return allChildrenHavePermission(child.id, level);
    });
  };

  const someChildrenHavePermission = (parentId: number, level: 'FULL' | 'READ' | 'NONE'): boolean => {
    const directChildren = menuItems.filter(item => item.parentId === parentId);
    if (directChildren.length === 0) return false;

    return directChildren.some(child => {
      // If child is a leaf item (level 3 or level 2 without children), check its direct permission
      if (isLeafItem(child.id)) {
        return getPermissionLevelForMenuItem(child.id) === level;
      }
      // Otherwise, recurse into child's children
      return someChildrenHavePermission(child.id, level);
    });
  };

  const getPermissionLevel = (menuItemId: number): 'FULL' | 'READ' | 'NONE' | 'PARTIAL' => {
    const menuItem = menuItems.find(item => item.id === menuItemId);
    if (!menuItem) return 'NONE';

    // Level 3 items always return their direct permission
    if (menuItem.level === 3) {
      return getPermissionLevelForMenuItem(menuItemId);
    }

    // Check if this is a "leaf" item (level 2 without level 3 children)
    const hasLevel3Children = menuItems.some(
      item => item.parentId === menuItemId && item.level === 3
    );

    // If level 2 item has no level 3 children, treat it as a final item
    if (menuItem.level === 2 && !hasLevel3Children) {
      return getPermissionLevelForMenuItem(menuItemId);
    }

    // For items with children, calculate based on children's permissions
    if (allChildrenHavePermission(menuItemId, 'FULL')) {
      return 'FULL';
    } else if (allChildrenHavePermission(menuItemId, 'READ')) {
      return 'READ';
    } else if (someChildrenHavePermission(menuItemId, 'FULL') || someChildrenHavePermission(menuItemId, 'READ')) {
      return 'PARTIAL';
    }

    return 'NONE';
  };

  const hasInheritedPermission = (menuItemId: number): boolean => {
    const menuItem = menuItems.find(item => item.id === menuItemId);
    if (!menuItem) return false;

    const directPermission = getPermissionLevelForMenuItem(menuItemId);
    if (directPermission !== 'NONE') {
      return false;
    }

    let currentParentId = menuItem.parentId;
    while (currentParentId) {
      const parent = menuItems.find(item => item.id === currentParentId);
      if (!parent) break;

      const parentHasPermission = allChildrenHavePermission(parent.id, 'FULL') || allChildrenHavePermission(parent.id, 'READ');
      if (parentHasPermission) {
        return true;
      }

      currentParentId = parent.parentId;
    }

    return false;
  };

  const handlePermissionChange = (
    menuItemId: number,
    level: 'FULL' | 'READ' | 'NONE',
    setFormData: (updater: (prev: RoleProfileFormData) => RoleProfileFormData) => void
  ) => {
    const menuItem = menuItems.find(item => item.id === menuItemId);
    if (!menuItem) return;

    const slugsToUpdate: string[] = [];

    if (menuItem.level === 1) {
      const groups = menuItems.filter(item => item.parentId === menuItemId);
      groups.forEach(group => {
        const groupItems = menuItems.filter(item => item.parentId === group.id && item.level === 3);
        if (groupItems.length > 0) {
          // Has level 3 children - update them
          groupItems.forEach(item => { if (item.slug) slugsToUpdate.push(item.slug); });
        } else {
          // No level 3 children - treat level 2 as final item (e.g., Apps menu)
          if (group.slug) slugsToUpdate.push(group.slug);
        }
      });
    } else if (menuItem.level === 2) {
      const groupItems = menuItems.filter(item => item.parentId === menuItemId && item.level === 3);
      if (groupItems.length > 0) {
        // Has level 3 children - update them
        groupItems.forEach(item => { if (item.slug) slugsToUpdate.push(item.slug); });
      } else {
        // No level 3 children - treat this level 2 item as final item
        if (menuItem.slug) slugsToUpdate.push(menuItem.slug);
      }
    } else if (menuItem.level === 3) {
      if (menuItem.slug) slugsToUpdate.push(menuItem.slug);
    }

    setFormData(prev => {
      const newPermissions = [...prev.permissions];

      slugsToUpdate.forEach(slug => {
        let existingPermission = newPermissions.find(p => p.moduleSlug === slug);

        if (!existingPermission) {
          existingPermission = {
            moduleSlug: slug,
            level: 'NONE'
          };
          newPermissions.push(existingPermission);
        }

        existingPermission.level = level;
      });

      return {
        ...prev,
        permissions: newPermissions
      };
    });
  };

  const generateAllPermissionsFromMenu = () => {
    const allPermissions: Array<{ moduleSlug: string; level: 'FULL' | 'READ' | 'NONE' }> = [];
    const processedSlugs = new Set<string>();

    menuItems.forEach(item => {
      if (!item.slug) return;

      // Include level 3 items
      if (item.level === 3 && !processedSlugs.has(item.slug)) {
        processedSlugs.add(item.slug);
        const permissionLevel = getPermissionLevelForMenuItem(item.id);
        allPermissions.push({
          moduleSlug: item.slug,
          level: permissionLevel,
        });
      }

      // Include level 2 items that have NO level 3 children (e.g., Apps menu items)
      if (item.level === 2 && !processedSlugs.has(item.slug)) {
        const hasChildren = menuItems.some(child => child.parentId === item.id && child.level === 3);
        if (!hasChildren) {
          processedSlugs.add(item.slug);
          const permissionLevel = getPermissionLevelForMenuItem(item.id);
          allPermissions.push({
            moduleSlug: item.slug,
            level: permissionLevel,
          });
        }
      }
    });

    return allPermissions;
  };

  return {
    getPermissionLevelForMenuItem,
    getPermissionLevel,
    hasInheritedPermission,
    handlePermissionChange,
    generateAllPermissionsFromMenu,
  };
}
